/**
 * @fileoverview Premium 4-Step Dashboard - Texas Property Tax Appeal Journey
 * @module StepsDashboardPage
 * 
 * Design Philosophy:
 * - Premium, professional aesthetic with depth and polish
 * - Clean visual hierarchy with purposeful use of color
 * - Cohesive slate/charcoal palette with warm teal accent
 * - Sophisticated progress visualization
 * - Reduced visual noise, increased clarity
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../api/client';
import { EvidencePacketDialog, NoticeReceivedDialog } from '../components/dialogs';
import { useEvidencePacketGate } from '../hooks/useEvidencePacketGate';
import { PropertyOnboarding, AppealTimeline } from '../components/dashboard';
import type { AppealTimelineData } from '../utils/appealTimeline';
import { getStepLockStatus, calculateCurrentPhase } from '../utils/appealTimeline';
import {
  FileText,
  ClipboardCheck,
  Search,
  MessageCircle,
  ChevronRight,
  CheckCircle2,
  Clock,
  ChevronDown,
  ChevronUp,
  LogOut,
  User,
  Home,
  Layers,
  Wind,
  Droplets,
  Zap,
  Video,
  ListChecks,
  Sparkles,
  ArrowRight,
  Mail,
  Monitor,
  Star,
  TrendingUp,
  Trophy,
  DollarSign,
  Edit3,
  X,
  PartyPopper,
  Calendar,
  Lock,
  BookOpen
} from 'lucide-react';

// Step metadata with streamlined styling
const STEP_METADATA = {
  1: { duration: 35, videoCount: 7, activityCount: 5 },  // Document Your Home
  2: { duration: 15, videoCount: 2, activityCount: 1 },  // File Your Appeal - Activity = Mark appeal as submitted
  3: { duration: 45, videoCount: 9, activityCount: 5 },
  4: { duration: 35, videoCount: 6, activityCount: 1 },
};

// Sub-item type definition
interface StepSubItem {
  key: string;
  label: string;
  icon: React.ElementType;
  route: string;
  state: Record<string, any>;
  isVideo?: boolean;
}

// Sub-item definitions
const STEP_SUBITEMS: Record<number, StepSubItem[]> = {
  1: [
    { key: 'videos', label: 'Intro Videos', icon: Video, route: '/step/1/document', state: { section: 'videos' }, isVideo: true },
    { key: 'ROOF', label: 'Roof', icon: Home, route: '/step/1/document', state: { autoExpand: 'ROOF' } },
    { key: 'FOUNDATION', label: 'Foundation', icon: Layers, route: '/step/1/document', state: { autoExpand: 'FOUNDATION' } },
    { key: 'HVAC', label: 'HVAC', icon: Wind, route: '/step/1/document', state: { autoExpand: 'HVAC' } },
    { key: 'PLUMBING', label: 'Plumbing', icon: Droplets, route: '/step/1/document', state: { autoExpand: 'PLUMBING' } },
    { key: 'ELECTRICAL', label: 'Electrical', icon: Zap, route: '/step/1/document', state: { autoExpand: 'ELECTRICAL' } },
  ],
  2: [
    { key: 'mail', label: 'Submit by Mail', icon: Mail, route: '/step/2/file-appeal', state: { section: 'mail' } },
    { key: 'portal', label: 'Submit Online', icon: Monitor, route: '/step/2/file-appeal', state: { section: 'portal' } },
    { key: 'form', label: 'Fill Out Form', icon: FileText, route: '/step/2/file-appeal', state: { section: 'form' } },
  ],
  3: [
    { key: 'intro', label: 'Intro Videos', icon: Video, route: '/step/3/find-flaws', state: { section: 'intro' }, isVideo: true },
    { key: 'WRONG_DETAILS', label: 'Wrong Details', icon: FileText, route: '/step/3/find-flaws', state: { flaw: 'WRONG_DETAILS' } },
    { key: 'QUALITY', label: 'Quality Issues', icon: Star, route: '/step/3/find-flaws', state: { flaw: 'QUALITY_MISCLASSIFICATION' } },
    { key: 'CONDITION', label: 'Condition', icon: ClipboardCheck, route: '/step/3/find-flaws', state: { flaw: 'CONDITION_MISCLASSIFICATION' } },
    { key: 'SALES', label: 'Sales Data', icon: TrendingUp, route: '/step/3/find-flaws', state: { flaw: 'OUTDATED_SALES' } },
  ],
  4: [
    { key: 'videos', label: 'Prep Videos', icon: Video, route: '/step/4/arguments', state: { section: 'videos' }, isVideo: true },
    { key: 'workup', label: 'View Evidence', icon: ListChecks, route: '/step/4/arguments', state: { view: 'workup' } },
    { key: 'generate', label: 'Generate Arguments', icon: Sparkles, route: '/step/4/arguments', state: { view: 'generate' } },
  ],
};

interface StepData {
  step: number;
  title: string;
  description: string;
  status: 'not_started' | 'in_progress' | 'completed';
  icon: React.ReactNode;
  route: string;
  actionLabel: string;
  progress?: {
    completed: number;
    total: number;
    details?: string[];
  } | undefined;
}

const StepsDashboardPage: React.FC = () => {
  const { user, logout, refreshToken } = useAuth();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [property, setProperty] = useState<any>(null);
  const [maintenanceReport, setMaintenanceReport] = useState<any>(null);
  const [flawFindings, setFlawFindings] = useState<any[]>([]);
  const [argumentStatus, setArgumentStatus] = useState<any>(null);
  const [step1Progress, setStep1Progress] = useState<number>(0);
  const [step2Progress, setStep2Progress] = useState<number>(0);
  const [step3Progress, setStep3Progress] = useState<number>(0);
  const [step4Progress, setStep4Progress] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [expandedStep, setExpandedStep] = useState<number | null>(null);
  const [showPropertyOnboarding, setShowPropertyOnboarding] = useState(false);
  
  // Getting Started intro module state
  const [introModuleContent, setIntroModuleContent] = useState<any[]>([]);
  const [introModuleProgress, setIntroModuleProgress] = useState<number>(0);
  const [isGettingStartedExpanded, setIsGettingStartedExpanded] = useState(true);
  
  // Appeal outcome state
  const [appealOutcome, setAppealOutcome] = useState<any>(null);
  const [showOutcomeForm, setShowOutcomeForm] = useState(false);
  
  // Transition video URL for evidence packet dialog
  const [transitionVideoUrl, setTransitionVideoUrl] = useState<string | undefined>(undefined);
  const [outcomeForm, setOutcomeForm] = useState({
    originalValue: '',
    finalValue: '',
    outcome: '',
    notes: ''
  });
  const [savingOutcome, setSavingOutcome] = useState(false);
  
  
  // Notice received dialog state
  const [showNoticeDialog, setShowNoticeDialog] = useState(false);

  // Load Google Fonts - Plus Jakarta Sans for consistent branding with LandingPage
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  // Evidence packet gate hook
  const evidenceGate = useEvidencePacketGate({
    appealOutcome,
    step1Complete: step1Progress >= 100
  });

  // Fetch all progress data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        const profileRes = await apiClient.users.getProfile();
        const profile = profileRes.data.data.profile || profileRes.data.data;
        if (profileRes.data.success && profile.properties?.length > 0) {
          const userProperty = profile.properties[0];
          setProperty(userProperty);
          setShowPropertyOnboarding(false);

          const [maintenanceRes, flawsRes, argumentRes, introModuleRes, step1ModuleRes, step2ModuleRes, step3ModuleRes, step4ModuleRes, outcomeRes] = await Promise.allSettled([
            apiClient.maintenance.getReportByProperty(userProperty.id),
            apiClient.flaws.getPropertyFindings(userProperty.id),
            apiClient.argument.getByProperty(userProperty.id),
            apiClient.courses.getModule('module-00', {}),
            apiClient.courses.getModule('module-01', {}),
            apiClient.courses.getModule('module-02', {}),
            apiClient.courses.getModule('module-03', {}),
            apiClient.courses.getModule('module-04', {}),
            apiClient.appealOutcome.getByProperty(userProperty.id),
          ]);

          if (maintenanceRes.status === 'fulfilled' && maintenanceRes.value.data.success) {
            setMaintenanceReport(maintenanceRes.value.data.data);
          }
          if (flawsRes.status === 'fulfilled' && flawsRes.value.data.success) {
            setFlawFindings(flawsRes.value.data.data || []);
          }
          if (argumentRes.status === 'fulfilled' && argumentRes.value.data.success) {
            setArgumentStatus(argumentRes.value.data.data);
          }
          // Get intro module (Getting Started) content and progress
          if (introModuleRes.status === 'fulfilled' && introModuleRes.value.data.success) {
            const introData = introModuleRes.value.data.data;
            setIntroModuleContent(introData.content || []);
            setIntroModuleProgress(introData.progress || 0);
            // Auto-collapse Getting Started if completed
            if (introData.progress >= 100) {
              setIsGettingStartedExpanded(false);
            }
          }
          // Get module progress for all 4 steps from UserProgress table
          if (step1ModuleRes.status === 'fulfilled' && step1ModuleRes.value.data.success) {
            const moduleProgress = step1ModuleRes.value.data.data.progress || 0;
            setStep1Progress(moduleProgress);
          }
          if (step2ModuleRes.status === 'fulfilled' && step2ModuleRes.value.data.success) {
            const moduleData = step2ModuleRes.value.data.data;
            const moduleProgress = moduleData.progress || 0;
            setStep2Progress(moduleProgress);
            
            // Extract transition video URL for evidence packet dialog
            if (moduleData.content) {
              const transitionContent = moduleData.content.find(
                (c: any) => c.metadata?.isTransitionVideo && c.contentUrl
              );
              if (transitionContent) {
                setTransitionVideoUrl(transitionContent.contentUrl);
              }
            }
          }
          if (step3ModuleRes.status === 'fulfilled' && step3ModuleRes.value.data.success) {
            const moduleProgress = step3ModuleRes.value.data.data.progress || 0;
            setStep3Progress(moduleProgress);
          }
          if (step4ModuleRes.status === 'fulfilled' && step4ModuleRes.value.data.success) {
            const moduleProgress = step4ModuleRes.value.data.data.progress || 0;
            setStep4Progress(moduleProgress);
          }
          // Get appeal outcome data
          if (outcomeRes.status === 'fulfilled' && outcomeRes.value.data.success && outcomeRes.value.data.data) {
            const outcome = outcomeRes.value.data.data;
            setAppealOutcome(outcome);
            // Pre-populate form with existing data
            setOutcomeForm({
              originalValue: outcome.originalValue ? (outcome.originalValue / 100).toString() : '',
              finalValue: outcome.finalValue ? (outcome.finalValue / 100).toString() : '',
              outcome: outcome.outcome || '',
              notes: outcome.notes || ''
            });
          }
        } else {
          // No property found - show onboarding
          setShowPropertyOnboarding(true);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchData();
    }
  }, [user]);

  // Calculate step statuses
  const getStepData = (): StepData[] => {
    const maintenanceCategories = ['ROOF', 'FOUNDATION', 'HVAC', 'PLUMBING', 'ELECTRICAL'];
    const completedCategories = maintenanceReport?.findings?.map((f: any) => f.category) || [];

    const flawTypes = ['WRONG_DETAILS', 'QUALITY_MISCLASSIFICATION', 'CONDITION_MISCLASSIFICATION', 'OUTDATED_SALES'];
    const completedFlaws = flawFindings.filter((f: any) => {
      const checkedCount = Object.values(f.checklistData || {}).filter(Boolean).length;
      return checkedCount > 0 || (f.notes && f.notes.length > 0);
    }).map((f: any) => f.flawType);

    // Use UserProgress as primary source, fall back to derived data
    // Step 1: UserProgress is primary source
    const step1Complete = step1Progress >= 100;
    const step1Started = step1Progress > 0;

    // Step 2: Use UserProgress if available, otherwise fall back to maintenance data
    const step2CompleteFromProgress = step2Progress >= 100;
    const step2StartedFromProgress = step2Progress > 0;
    const step2CompleteFromData = completedCategories.length >= 3;
    const step2StartedFromData = completedCategories.length > 0;
    const step2Complete = step2CompleteFromProgress || step2CompleteFromData;
    const step2Started = step2StartedFromProgress || step2StartedFromData;

    // Step 3: Use UserProgress if available, otherwise fall back to flaw findings
    const step3CompleteFromProgress = step3Progress >= 100;
    const step3StartedFromProgress = step3Progress > 0;
    const step3CompleteFromData = completedFlaws.length >= 3;
    const step3StartedFromData = completedFlaws.length > 0;
    const step3Complete = step3CompleteFromProgress || step3CompleteFromData;
    const step3Started = step3StartedFromProgress || step3StartedFromData;

    // Step 4: Use UserProgress if available, otherwise fall back to argument status
    const step4CompleteFromProgress = step4Progress >= 100;
    const step4StartedFromProgress = step4Progress > 0;
    const step4CompleteFromData = Boolean(argumentStatus?.generatedContent);
    const step4StartedFromData = argumentStatus !== null && argumentStatus !== undefined;
    const step4Complete = step4CompleteFromProgress || step4CompleteFromData;
    const step4Started = step4StartedFromProgress || step4StartedFromData;

    const getCurrentStatus = (stepNum: number): 'not_started' | 'in_progress' | 'completed' => {
      switch (stepNum) {
        case 1: // Document Your Home (uses step2Progress from module-02)
          if (step2Complete || step3Started || step4Started) return 'completed';
          if (step2Started) return 'in_progress';
          return 'not_started';
        case 2: // File Your Appeal (uses step1Progress from module-01)
          if (step1Complete) return 'completed';
          if (step1Started) return 'in_progress';
          return 'not_started';
        case 3:
          if (step3Complete || step4Started) return 'completed';
          if (step3Started) return 'in_progress';
          return 'not_started';
        case 4:
          if (step4Complete) return 'completed';
          if (step4Started) return 'in_progress';
          return 'not_started';
        default:
          return 'not_started';
      }
    };

    // Step 2 progress (File Appeal): Only activity completion matters (100% = appeal submitted)
    // Videos are supplementary and don't affect completion status

    return [
      {
        step: 1,
        title: 'Document Your Home',
        description: 'Complete maintenance checklists for roof, foundation, HVAC, plumbing, and electrical systems.',
        status: getCurrentStatus(1),
        icon: <ClipboardCheck className="w-5 h-5" />,
        route: '/step/1/document',
        actionLabel: step2Complete ? 'Review' : step2Started ? 'Continue' : 'Start',
        progress: {
          completed: completedCategories.length,
          total: maintenanceCategories.length,
          details: completedCategories,
        },
      },
      {
        step: 2,
        title: 'File Your Appeal',
        description: 'Submit your protest form via mail or online portal. Learn exactly how to fill out the form correctly.',
        status: getCurrentStatus(2),
        icon: <FileText className="w-5 h-5" />,
        route: '/step/2/file-appeal',
        actionLabel: step1Complete ? 'Review' : step1Started ? 'Continue' : 'Start Guide',
        // Step 2 (File Appeal) progress is binary: either complete (1/1) or not started
        progress: step1Complete ? {
          completed: 1,
          total: 1,
          details: ['Appeal submitted']
        } : undefined,
      },
      {
        step: 3,
        title: 'Find County Flaws',
        description: 'Analyze the county\'s evidence and identify errors in their property valuation.',
        status: getCurrentStatus(3),
        icon: <Search className="w-5 h-5" />,
        route: '/step/3/find-flaws',
        actionLabel: step3Complete ? 'Review' : step3Started ? 'Continue' : 'Start',
        progress: {
          completed: completedFlaws.length,
          total: flawTypes.length,
          details: completedFlaws,
        },
      },
      {
        step: 4,
        title: 'Prepare Your Case',
        description: 'Generate talking points for your informal meeting with the appraiser.',
        status: getCurrentStatus(4),
        icon: <MessageCircle className="w-5 h-5" />,
        route: '/step/4/arguments',
        actionLabel: step4Complete ? 'Review' : step4Started ? 'Continue' : 'Start',
        ...(step4Complete && {
          progress: { completed: 1, total: 1, details: ['Arguments generated'] }
        }),
      },
    ];
  };

  const steps = getStepData();
  const completedSteps = steps.filter(s => s.status === 'completed').length;
  const allStepsCompleted = completedSteps === 4;
  const inProgressStep = steps.find(s => s.status === 'in_progress');
  const nextStep = steps.find(s => s.status === 'not_started');

  // Timeline data for AppealTimeline component - defined early so it can be used in getCurrentStepByPhase
  const timelineData: AppealTimelineData = useMemo(() => ({
    signupDate: user?.createdAt ? new Date(user.createdAt) : null,
    noticeDate: property?.lastAppraisalNoticeDate ? new Date(property.lastAppraisalNoticeDate) : null,
    protestFiled: step1Progress >= 100,
    evidencePacketReceived: appealOutcome?.evidencePacketReceived || false,
    hearingDate: appealOutcome?.hearingDate ? new Date(appealOutcome.hearingDate) : null,
    outcomeRecorded: appealOutcome?.outcome ? true : false,
    introCompleted: introModuleProgress >= 100,
    documentingCompleted: step2Progress >= 100 || (maintenanceReport?.findings?.length >= 5),
  }), [user?.createdAt, property?.lastAppraisalNoticeDate, step1Progress, step2Progress, maintenanceReport, appealOutcome, introModuleProgress]);
  
  // Determine the current step based on timeline phase
  // Pre-notice phase (prepare): Highlight Step 1 (Document Your Home) - can start anytime
  // File phase: Highlight Step 2 (File Protest) - it's urgent once notice arrives
  // Build case phase: Continue with normal in-progress/next step logic
  const getCurrentStepByPhase = () => {
    if (allStepsCompleted) return steps[3];
    
    const phaseResult = calculateCurrentPhase(timelineData);
    
    // In prepare phase (pre-notice), Step 1 (Document) is the recommended action
    if (phaseResult.phase === 'prepare') {
      return steps[0]; // Step 1: Document Your Home (index 0)
    }
    
    // In file phase (notice received, not filed), Step 2 (File) is urgent
    if (phaseResult.phase === 'file') {
      return steps[1]; // Step 2: File Your Protest (index 1)
    }
    
    // In build_case phase, find the next actionable step
    if (phaseResult.phase === 'build_case') {
      // If Step 1 (Document) is not complete, prioritize it
      if (steps[0].status !== 'completed') {
        return steps[0];
      }
      // Otherwise, Step 3
      if (steps[2].status !== 'completed') {
        return steps[2];
      }
    }
    
    // In hearing phase, Step 4 is the focus
    if (phaseResult.phase === 'hearing') {
      return steps[3]; // Step 4: Prepare Your Case (index 3)
    }
    
    // Default fallback to original logic
    return inProgressStep || nextStep || steps[0];
  };
  
  const currentStep = getCurrentStepByPhase();
  
  const calculateOverallProgress = () => {
    let totalProgress = 0;
    steps.forEach(step => {
      if (step.status === 'completed') {
        totalProgress += 25;
      } else if (step.status === 'in_progress' && step.progress) {
        totalProgress += (step.progress.completed / step.progress.total) * 25;
      }
    });
    return Math.round(totalProgress);
  };

  const overallProgress = calculateOverallProgress();

  // HIDDEN: Time remaining calculation - may bring back later if needed
  // const calculateTimeRemaining = () => {
  //   let remaining = 0;
  //   steps.forEach(step => {
  //     const meta = STEP_METADATA[step.step as keyof typeof STEP_METADATA];
  //     if (step.status === 'not_started') {
  //       remaining += meta.duration;
  //     } else if (step.status === 'in_progress' && step.progress) {
  //       remaining += Math.round(meta.duration * (1 - step.progress.completed / step.progress.total));
  //     }
  //   });
  //   return remaining;
  // };
  // const timeRemaining = calculateTimeRemaining();

  const handleSignOut = () => {
    logout();
    navigate('/');
  };

  // Handle property onboarding completion
  const handlePropertyOnboardingComplete = useCallback(async () => {
    // Refresh user profile to get the new property
    try {
      await refreshToken();
      // Re-fetch data with the new property
      setLoading(true);
      const profileRes = await apiClient.users.getProfile();
      const profile = profileRes.data.data.profile || profileRes.data.data;
      if (profileRes.data.success && profile.properties?.length > 0) {
        const userProperty = profile.properties[0];
        setProperty(userProperty);
        setShowPropertyOnboarding(false);
      }
    } catch (error) {
      console.error('Error refreshing after property onboarding:', error);
    } finally {
      setLoading(false);
    }
  }, [refreshToken]);

  // Save appeal outcome
  const handleSaveOutcome = async () => {
    if (!property) return;
    
    setSavingOutcome(true);
    try {
      const data: any = {
        propertyId: property.id,
        appealYear: new Date().getFullYear()
      };
      
      // Convert dollar values to cents
      if (outcomeForm.originalValue) {
        data.originalValue = Math.round(parseFloat(outcomeForm.originalValue) * 100);
      }
      if (outcomeForm.finalValue) {
        data.finalValue = Math.round(parseFloat(outcomeForm.finalValue) * 100);
      }
      if (outcomeForm.outcome) {
        data.outcome = outcomeForm.outcome;
      }
      if (outcomeForm.notes) {
        data.notes = outcomeForm.notes;
      }
      
      const response = await apiClient.appealOutcome.save(data);
      if (response.data.success) {
        setAppealOutcome(response.data.data);
        setShowOutcomeForm(false);
        toast.success('Appeal outcome saved!');
      }
    } catch (error) {
      console.error('Error saving appeal outcome:', error);
    } finally {
      setSavingOutcome(false);
    }
  };

  // Calculate savings for display
  const calculateDisplaySavings = () => {
    if (!outcomeForm.originalValue || !outcomeForm.finalValue) return null;
    const original = parseFloat(outcomeForm.originalValue);
    const final = parseFloat(outcomeForm.finalValue);
    if (isNaN(original) || isNaN(final)) return null;
    const savings = original - final;
    const taxSavings = savings * 0.025; // Approximate Texas tax rate
    return { savings, taxSavings };
  };

  // Handle navigation to Step 3 with evidence packet gate
  const handleNavigateToStep3 = (state?: Record<string, any>) => {
    if (evidenceGate.shouldShowDialog) {
      evidenceGate.showDialog();
    } else {
      navigate('/step/3/find-flaws', state ? { state } : undefined);
    }
  };

  // Handle successful evidence packet dialog completion
  // Stay on dashboard - user can navigate to Step 3 when ready
  const handleEvidenceDialogContinue = () => {
    evidenceGate.hideDialog();
    // Refetch appeal outcome so timelineData stays in sync
    if (property) {
      apiClient.appealOutcome.getByProperty(property.id).then(res => {
        if (res.data.success && res.data.data) {
          setAppealOutcome(res.data.data);
        }
      }).catch(() => { /* non-critical */ });
    }
  };

  // Handle evidence packet dialog skip
  // Stay on dashboard - user chose to skip, they can proceed to Step 3 later
  const handleEvidenceDialogSkip = () => {
    evidenceGate.markSessionSkipped();
  };


  // Handle notice received dialog success
  const handleNoticeReceived = useCallback((noticeDate: Date, assessedValue: number) => {
    // Update property state with new notice info - this will unlock Step 2
    setProperty((prev: any) => ({
      ...prev,
      lastAppraisalNoticeDate: noticeDate.toISOString(),
      assessedValue: assessedValue
    }));
    setShowNoticeDialog(false);
  }, []);

  // Handle click on locked Step 2 card
  const handleLockedStep2Click = useCallback(() => {
    setShowNoticeDialog(true);
  }, []);

  const toggleStepExpanded = (stepNum: number) => {
    setExpandedStep(expandedStep === stepNum ? null : stepNum);
  };

  useEffect(() => {
    if (!loading && currentStep) {
      setExpandedStep(currentStep.step);
    }
  }, [loading]);

  // Load fonts
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@500&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => {
      if (document.head.contains(link)) {
        document.head.removeChild(link);
      }
    };
  }, []);

  // Check if a sub-item is completed
  const isSubItemCompleted = (stepNum: number, key: string) => {
    if (stepNum === 1) {
      // Step 1: Document Your Home - check maintenance report for category completion
      return (maintenanceReport?.findings?.map((f: any) => f.category) || []).includes(key);
    }
    if (stepNum === 2) {
      // Step 2 (File Appeal) completion is binary: all sub-items are complete when appeal is submitted
      // The sub-items (mail, portal, form) are just navigation shortcuts, not individual checkpoints
      return step1Progress >= 100;
    }
    if (stepNum === 3) {
      const completedFlaws = flawFindings.filter((f: any) => {
        const checkedCount = Object.values(f.checklistData || {}).filter(Boolean).length;
        return checkedCount > 0 || (f.notes && f.notes.length > 0);
      }).map((f: any) => f.flawType);
      const keyMap: Record<string, string> = {
        'WRONG_DETAILS': 'WRONG_DETAILS',
        'QUALITY': 'QUALITY_MISCLASSIFICATION',
        'CONDITION': 'CONDITION_MISCLASSIFICATION',
        'SALES': 'OUTDATED_SALES',
      };
      return completedFlaws.includes(keyMap[key] || key);
    }
    if (stepNum === 4 && key === 'generate') {
      return Boolean(argumentStatus?.generatedContent);
    }
    return false;
  };

  const getCompletionSummary = (step: StepData) => {
    if (step.step === 1 && maintenanceReport) {
      // Step 1: Document Your Home
      const parts = [];
      parts.push(`${maintenanceReport.findings?.length || 0}/5 areas`);
      if (maintenanceReport.totalEstimatedCost > 0) {
        parts.push(`$${(maintenanceReport.totalEstimatedCost / 100).toLocaleString()}`);
      }
      const totalPhotos = maintenanceReport.findings?.reduce((sum: number, f: any) => sum + (f.evidence?.length || 0), 0) || 0;
      if (totalPhotos > 0) {
        parts.push(`${totalPhotos} photos`);
      }
      return parts.join(' · ');
    }
    if (step.step === 2 && step1Progress >= 100) {
      // Step 2: File Your Appeal
      return 'Appeal submitted';
    }
    if (step.step === 3 && flawFindings.length > 0) {
      const completedCount = flawFindings.filter((f: any) => {
        const checkedCount = Object.values(f.checklistData || {}).filter(Boolean).length;
        return checkedCount > 0 || (f.notes && f.notes.length > 0);
      }).length;
      return `${completedCount}/5 analyzed`;
    }
    if (step.step === 4 && argumentStatus?.generatedContent) {
      return 'Ready for your meeting';
    }
    return null;
  };

  // Show property onboarding if user has no property
  if (showPropertyOnboarding && !loading) {
    return (
      <div className="min-h-screen bg-slate-50" style={{ fontFamily: "'Plus Jakarta Sans', Inter, sans-serif" }}>
        {/* Subtle background pattern */}
        <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-100 via-slate-50 to-white pointer-events-none" />
        
        {/* Header - Same as main dashboard */}
        <nav className="sticky top-0 w-full bg-gray-900/95 backdrop-blur-md z-50 border-b border-gray-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-[4.25rem]">
              {/* Logo */}
              <div className="flex items-end gap-2.5 group cursor-pointer select-none">
                <img 
                  src="/logo-104.png" 
                  alt="" 
                  className="w-[34px] h-[34px] object-contain transition-transform duration-200 group-hover:scale-105 mb-[2px]"
                />
                <div className="flex flex-col">
                  <span 
                    className="text-[1rem] leading-[1.2] tracking-[-0.015em] text-white"
                    style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                  >
                    <span className="font-semibold">Appeal Your</span>
                    {' '}
                    <span className="font-extrabold text-teal-400">Home</span>
                  </span>
                  <span 
                    className="text-[0.54rem] font-semibold uppercase tracking-[0.14em] text-gray-400"
                    style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", marginTop: '2px' }}
                  >
                    Learn. Advocate. Save.
                  </span>
                </div>
              </div>

              {/* User Menu */}
              <div className="relative">
                <button 
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 text-white hover:text-teal-400 transition-colors p-2 rounded-lg hover:bg-gray-800"
                >
                  <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center ring-2 ring-gray-600">
                    <span className="text-white font-semibold text-sm">
                      {user?.firstName?.charAt(0) || 'U'}
                    </span>
                  </div>
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                </button>

                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-2xl border border-gray-200 py-2 z-50">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-sm font-semibold text-gray-900">{user?.firstName} {user?.lastName}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{user?.email}</p>
                    </div>
                    <button
                      onClick={() => { setShowUserMenu(false); handleSignOut(); }}
                      className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-3 transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Sign Out</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </nav>

        <div className="relative">
          <PropertyOnboarding 
            onComplete={handlePropertyOnboardingComplete}
            userName={user?.firstName}
          />
        </div>
      </div>
    );
  }

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" style={{ fontFamily: "'Plus Jakarta Sans', Inter, sans-serif" }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: "'Plus Jakarta Sans', Inter, sans-serif" }}>
      {/* Subtle background pattern */}
      <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-100 via-slate-50 to-white pointer-events-none" />
      
      {/* Header - Aligned with LandingPage Navigation */}
      <nav className="sticky top-0 w-full bg-gray-900/95 backdrop-blur-md z-50 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-[4.25rem]">
            {/* Logo - Premium brand treatment matching LandingPage */}
            <div 
              className="flex items-end gap-2.5 group cursor-pointer select-none"
              onClick={() => navigate('/dashboard')}
            >
              {/* Logo mark - sized to span both text lines, bottom aligned */}
              <img 
                src="/logo-104.png" 
                alt="" 
                className="w-[34px] h-[34px] object-contain transition-transform duration-200 group-hover:scale-105 mb-[2px]"
              />
              
              {/* Refined wordmark */}
              <div className="flex flex-col">
                <span 
                  className="text-[1rem] leading-[1.2] tracking-[-0.015em] text-white"
                  style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                >
                  <span className="font-semibold">Appeal Your</span>
                  {' '}
                  <span className="font-extrabold text-teal-400">Home</span>
                </span>
                <span 
                  className="text-[0.54rem] font-semibold uppercase tracking-[0.14em] text-gray-400"
                  style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", marginTop: '2px' }}
                >
                  Learn. Advocate. Save.
                </span>
              </div>
            </div>

            {/* User Menu */}
            <div className="relative">
              <button 
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-2 text-white hover:text-teal-400 transition-colors p-2 rounded-lg hover:bg-gray-800"
              >
                <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center ring-2 ring-gray-600">
                  <span className="text-white font-semibold text-sm">
                    {user?.firstName?.charAt(0) || 'U'}
                  </span>
                </div>
                <ChevronDown className="w-4 h-4 text-gray-400" />
              </button>

              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-2xl border border-gray-200 py-2 z-50 animate-in fade-in slide-in-from-top-1">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-semibold text-gray-900">{user?.firstName} {user?.lastName}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{user?.email}</p>
                    <div className="flex items-center gap-1 mt-2">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        user?.tier === 'ai_enhanced' 
                          ? 'bg-teal-100 text-teal-700' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {user?.tier === 'ai_enhanced' ? '✨ AI Enhanced' : 'Standard'}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => { setShowUserMenu(false); navigate('/profile'); }}
                    className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
                  >
                    <User className="w-4 h-4 text-gray-400" />
                    <span>Profile Settings</span>
                  </button>
                  <button
                    onClick={() => { setShowUserMenu(false); handleSignOut(); }}
                    className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-3 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Sign Out</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Appeal Timeline - Shows current phase and what to do next */}
        <AppealTimeline data={timelineData} />

        {/* Getting Started Section - Course Introduction (matches step card structure) */}
        {introModuleContent.length > 0 && (
          <div className={`mb-3 bg-white rounded-xl border transition-all duration-200 overflow-hidden ${
            introModuleProgress >= 100 
              ? 'border-emerald-200 bg-gradient-to-r from-emerald-50/50 to-white'
              : introModuleProgress > 0 || isGettingStartedExpanded
                ? 'border-teal-200 shadow-md shadow-teal-500/5 ring-1 ring-teal-100'
                : 'border-slate-200'
          }`}>
            {/* Header */}
            <div 
              className="p-4 sm:p-5 cursor-pointer"
              onClick={() => setIsGettingStartedExpanded(!isGettingStartedExpanded)}
            >
              <div className="flex items-center gap-4">
                {/* Icon */}
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  introModuleProgress >= 100
                    ? 'bg-emerald-100 text-emerald-600'
                    : introModuleProgress > 0 || isGettingStartedExpanded
                      ? 'bg-teal-100 text-teal-600'
                      : 'bg-slate-100 text-slate-400'
                }`}>
                  {introModuleProgress >= 100 ? (
                    <CheckCircle2 className="w-6 h-6" />
                  ) : (
                    <BookOpen className="w-6 h-6" />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className={`font-semibold ${
                      introModuleProgress >= 100 
                        ? 'text-emerald-900' 
                        : introModuleProgress > 0 || isGettingStartedExpanded
                          ? 'text-slate-900'
                          : 'text-slate-600'
                    }`}>
                      Getting Started
                    </h3>
                    {introModuleProgress >= 100 ? (
                      <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded uppercase tracking-wider">
                        Complete
                      </span>
                    ) : introModuleProgress > 0 ? (
                      <span className="text-[10px] font-bold text-teal-700 bg-teal-100 px-2 py-0.5 rounded uppercase tracking-wider">
                        In Progress
                      </span>
                    ) : (
                      <span className="text-[10px] font-bold text-teal-700 bg-teal-100 px-2 py-0.5 rounded uppercase tracking-wider">
                        Start Here
                      </span>
                    )}
                  </div>
                  
                  {/* Meta info */}
                  <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      6 min
                    </span>
                    <span>·</span>
                    <span>2 videos</span>
                  </div>

                  {/* Progress bar - matches step cards */}
                  {introModuleProgress > 0 && introModuleProgress < 100 && (
                    <div className="flex items-center gap-2 mt-2">
                      <div className="flex-1 max-w-[120px] h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-teal-400 rounded-full transition-all"
                          style={{ width: `${introModuleProgress}%` }}
                        />
                      </div>
                      <span className="text-xs font-medium text-teal-600">
                        {Math.round(introModuleProgress)}%
                      </span>
                    </div>
                  )}
                  
                  {/* Completion summary */}
                  {introModuleProgress >= 100 && (
                    <p className="text-xs text-emerald-600 font-medium mt-1">2/2 videos watched</p>
                  )}
                </div>

                {/* Actions - matches step cards */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/intro/getting-started');
                    }}
                    className={`hidden sm:flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      introModuleProgress >= 100
                        ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                        : introModuleProgress > 0 || isGettingStartedExpanded
                          ? 'bg-slate-900 text-white hover:bg-slate-800'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {introModuleProgress >= 100 ? 'Review' : introModuleProgress > 0 ? 'Continue' : 'Start'}
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsGettingStartedExpanded(!isGettingStartedExpanded);
                    }}
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    {isGettingStartedExpanded ? (
                      <ChevronUp className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Expanded Content - matches step cards structure */}
            <div className={`transition-all duration-300 ease-in-out ${
              isGettingStartedExpanded ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
            }`}>
              <div className="px-4 sm:px-5 pb-5 pt-3 border-t border-slate-100">
                <p className="text-sm text-slate-600 mb-4">
                  Watch these two short videos to understand the course and the Texas property tax appeal timeline.
                </p>
                
                {/* Sub-items grid - matches step cards */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {introModuleContent
                    .sort((a, b) => (a.metadata?.sectionOrder || 0) - (b.metadata?.sectionOrder || 0))
                    .map((content, index) => {
                      const isWatched = introModuleProgress >= ((index + 1) / introModuleContent.length) * 100;
                      
                      return (
                        <button
                          key={content.id}
                          onClick={() => navigate('/intro/getting-started', { 
                            state: { videoId: content.id, autoPlay: true } 
                          })}
                          className={`flex items-center gap-2.5 p-3 rounded-lg text-left transition-all text-sm ${
                            isWatched 
                              ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                              : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
                          }`}
                        >
                          <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${
                            isWatched ? 'bg-emerald-200' : 'bg-slate-200'
                          }`}>
                            {isWatched ? (
                              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" />
                            ) : (
                              <Video className="w-3.5 h-3.5 text-teal-600" />
                            )}
                          </div>
                          <span className="font-medium truncate">
                            {index === 0 ? 'Welcome' : 'Timeline'}
                          </span>
                        </button>
                      );
                    })}
                </div>

                {/* Mobile CTA - matches step cards */}
                <button
                  onClick={() => navigate('/intro/getting-started')}
                  className={`sm:hidden w-full mt-4 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
                    introModuleProgress >= 100
                      ? 'bg-emerald-100 text-emerald-700'
                      : introModuleProgress > 0
                        ? 'bg-slate-900 text-white'
                        : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  {introModuleProgress >= 100 ? 'Review' : introModuleProgress > 0 ? 'Continue' : 'Start'}
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Savings Display - Show when outcome recorded with savings */}
        {appealOutcome?.finalValue && appealOutcome?.savings > 0 && (
          <div className="bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 rounded-2xl shadow-sm border border-emerald-200/60 overflow-hidden mb-6 p-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">Your Assessment Reduction</p>
                  <p className="text-2xl font-bold text-emerald-600">
                    ${(appealOutcome.savings / 100).toLocaleString()}
                  </p>
                  {appealOutcome.taxSavings && (
                    <p className="text-xs text-slate-500">
                      ≈ ${(appealOutcome.taxSavings / 100).toLocaleString()} annual tax savings
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={() => setShowOutcomeForm(true)}
                className="inline-flex items-center gap-2 text-emerald-700 hover:text-emerald-800 font-medium text-sm"
              >
                <Edit3 className="w-4 h-4" />
                Update Results
              </button>
            </div>
          </div>
        )}

        {/* Steps */}
        <div className="space-y-3">
          {steps.map((step) => {
            const meta = STEP_METADATA[step.step as keyof typeof STEP_METADATA];
            const isExpanded = expandedStep === step.step;
            const isCurrentStep = step.step === currentStep.step;
            const subItems = STEP_SUBITEMS[step.step];
            const summary = getCompletionSummary(step);
            // Get timeline-based lock status and badge for this step
            const lockStatus = getStepLockStatus(step.step, timelineData);

            return (
              <div
                key={step.step}
                className={`bg-white rounded-xl border transition-all duration-200 overflow-hidden ${
                  step.status === 'completed' 
                    ? 'border-emerald-200 bg-gradient-to-r from-emerald-50/50 to-white'
                    : lockStatus.isLocked
                      ? 'border-slate-200'
                      : isCurrentStep
                        ? 'border-teal-200 shadow-md shadow-teal-500/5 ring-1 ring-teal-100'
                        : 'border-slate-200'
                }`}
              >
                {/* Step Header */}
                <div 
                  className="p-4 sm:p-5 cursor-pointer"
                  onClick={() => toggleStepExpanded(step.step)}
                >
                  <div className="flex items-center gap-4">
                    {/* Step Number/Icon */}
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      step.status === 'completed' 
                        ? 'bg-emerald-100 text-emerald-600'
                        : lockStatus.isLocked
                          ? 'bg-slate-100 text-slate-300'
                          : isCurrentStep
                            ? 'bg-teal-100 text-teal-600'
                            : 'bg-slate-100 text-slate-400'
                    }`}>
                      {step.status === 'completed' ? (
                        <CheckCircle2 className="w-6 h-6" />
                      ) : (
                        step.icon
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className={`font-semibold ${
                          step.status === 'completed' 
                            ? 'text-emerald-900' 
                            : lockStatus.isLocked
                              ? 'text-slate-400'
                              : isCurrentStep 
                                ? 'text-slate-900'
                                : 'text-slate-600'
                        }`}>
                          {step.title}
                        </h3>
                        {/* Timeline Context Badge */}
                        {lockStatus.badge && step.status !== 'completed' && (
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider ${
                            lockStatus.isLocked 
                              ? 'text-slate-500 bg-slate-100'
                              : lockStatus.badge.includes('day') && !lockStatus.badge.includes('Anytime')
                                ? lockStatus.badge.includes('1 day') || lockStatus.badge.includes('2 day') || lockStatus.badge.includes('3 day')
                                  ? 'text-red-700 bg-red-100'
                                  : 'text-amber-700 bg-amber-100'
                                : step.step === 1
                                  ? 'text-emerald-700 bg-emerald-100'
                                  : 'text-slate-600 bg-slate-100'
                          }`}>
                            {lockStatus.badge}
                          </span>
                        )}
                        {isCurrentStep && step.status !== 'completed' && !lockStatus.badge && (
                          <span className="text-[10px] font-bold text-teal-700 bg-teal-100 px-2 py-0.5 rounded uppercase tracking-wider">
                            Current
                          </span>
                        )}
                      </div>
                      
                      {/* Meta info */}
                      <div className={`flex items-center gap-3 mt-1 text-xs ${lockStatus.isLocked ? 'text-slate-400' : 'text-slate-500'}`}>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {meta.duration} min
                        </span>
                        <span>·</span>
                        <span>{meta.videoCount} videos</span>
                        {meta.activityCount > 0 && (
                          <>
                            <span>·</span>
                            <span>{meta.activityCount} activities</span>
                          </>
                        )}
                      </div>

                      {/* Lock reason - shown when step is locked or has timeline context */}
                      {lockStatus.reason && step.status !== 'completed' && (
                        <p className="text-xs text-slate-400 mt-1 italic">{lockStatus.reason}</p>
                      )}

                      {/* Progress or Summary */}
                      {step.progress && step.status === 'in_progress' && !lockStatus.isLocked && (
                        <div className="flex items-center gap-2 mt-2">
                          <div className="flex-1 max-w-[120px] h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-teal-400 rounded-full transition-all"
                              style={{ width: `${(step.progress.completed / step.progress.total) * 100}%` }}
                            />
                          </div>
                          <span className="text-xs font-medium text-teal-600">
                            {step.progress.completed}/{step.progress.total}
                          </span>
                        </div>
                      )}
                      {summary && step.status === 'completed' && (
                        <p className="text-xs text-emerald-600 font-medium mt-1">{summary}</p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {!lockStatus.isLocked ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            // Use gate for Step 3 navigation
                            if (step.step === 3) {
                              handleNavigateToStep3();
                            } else {
                              navigate(step.route);
                            }
                          }}
                          className={`hidden sm:flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                            step.status === 'completed'
                              ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                              : isCurrentStep
                                ? 'bg-slate-900 text-white hover:bg-slate-800'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {step.actionLabel}
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      ) : step.step === 2 ? (
                        // Step 2 is locked but can be unlocked by indicating notice received
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLockedStep2Click();
                          }}
                          className="hidden sm:flex items-center gap-1.5 px-5 py-2.5 rounded-lg text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 transition-all shadow-md"
                        >
                          Notice Received
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      ) : (
                        <span className="hidden sm:flex items-center justify-center w-10 h-10 rounded-lg bg-slate-50 text-slate-300 cursor-not-allowed">
                          <Lock className="w-5 h-5" />
                        </span>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleStepExpanded(step.step);
                        }}
                        className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                      >
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-slate-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-slate-400" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                <div className={`transition-all duration-300 ease-in-out ${
                  isExpanded ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
                }`}>
                  <div className="px-4 sm:px-5 pb-5 pt-3 border-t border-slate-100">
                    <p className="text-sm text-slate-600 mb-4">{step.description}</p>
                    
                    {/* Sub-items */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {subItems.map((item) => {
                        const Icon = item.icon;
                        const isCompleted = isSubItemCompleted(step.step, item.key);
                        
                        return (
                          <button
                            key={item.key}
                            disabled={lockStatus.isLocked}
                            onClick={() => {
                              if (lockStatus.isLocked) return;
                              // Use gate for Step 3 navigation
                              if (step.step === 3) {
                                handleNavigateToStep3(item.state);
                              } else {
                                navigate(item.route, { state: item.state });
                              }
                            }}
                            className={`flex items-center gap-2.5 p-3 rounded-lg text-left transition-all text-sm ${
                              lockStatus.isLocked
                                ? 'bg-slate-50 text-slate-300 cursor-not-allowed'
                                : isCompleted 
                                  ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                                  : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
                            }`}
                          >
                            <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${
                              lockStatus.isLocked
                                ? 'bg-slate-100'
                                : isCompleted ? 'bg-emerald-200' : 'bg-slate-200'
                            }`}>
                              {lockStatus.isLocked ? (
                                <Lock className="w-3.5 h-3.5 text-slate-300" />
                              ) : isCompleted ? (
                                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" />
                              ) : (
                                <Icon className={`w-3.5 h-3.5 ${item.isVideo ? 'text-teal-600' : 'text-slate-500'}`} />
                              )}
                            </div>
                            <span className="font-medium truncate">{item.label}</span>
                          </button>
                        );
                      })}
                    </div>

                    {/* Mobile CTA */}
                    {!lockStatus.isLocked ? (
                      <button
                        onClick={() => {
                          // Use gate for Step 3 navigation
                          if (step.step === 3) {
                            handleNavigateToStep3();
                          } else {
                            navigate(step.route);
                          }
                        }}
                        className={`sm:hidden w-full mt-4 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
                          step.status === 'completed'
                            ? 'bg-emerald-100 text-emerald-700'
                            : isCurrentStep
                              ? 'bg-slate-900 text-white'
                              : 'bg-slate-100 text-slate-600'
                        }`}
                      >
                        {step.actionLabel}
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    ) : step.step === 2 ? (
                      // Step 2 locked - show button to indicate notice received
                      <button
                        onClick={handleLockedStep2Click}
                        className="sm:hidden w-full mt-4 flex items-center justify-center gap-2 px-4 py-3.5 rounded-lg bg-slate-900 text-white font-semibold shadow-md"
                      >
                        Notice Received
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    ) : (
                      <div className="sm:hidden w-full mt-4 flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-slate-50 text-slate-400 font-medium">
                        <Lock className="w-4 h-4" />
                        <span>{lockStatus.reason || 'Locked'}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

      </div>

      {/* Mobile Sticky CTA */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 p-4 bg-white/95 backdrop-blur-sm border-t border-slate-200 z-40">
        {allStepsCompleted ? (
          <button
            onClick={() => appealOutcome?.finalValue ? setShowOutcomeForm(true) : setShowOutcomeForm(true)}
            className="w-full flex items-center justify-center gap-2 bg-emerald-600 text-white font-semibold px-6 py-4 rounded-xl"
          >
            {appealOutcome?.finalValue ? 'Update Results' : 'Record Your Results'}
            <Edit3 className="w-5 h-5" />
          </button>
        ) : (() => {
          // Respect lock status and use proper navigation handlers for mobile CTA
          const mobileLockStatus = getStepLockStatus(currentStep.step, timelineData);
          
          if (mobileLockStatus.isLocked && currentStep.step === 2) {
            // Step 2 locked: show "Notice Received" button like desktop
            return (
              <button
                onClick={handleLockedStep2Click}
                className="w-full flex items-center justify-center gap-2 bg-slate-900 text-white font-semibold px-6 py-4 rounded-xl"
              >
                Notice Received
                <ChevronRight className="w-5 h-5" />
              </button>
            );
          }
          
          if (mobileLockStatus.isLocked) {
            // Other locked steps: show locked state
            return (
              <div className="w-full flex items-center justify-center gap-2 bg-slate-200 text-slate-400 font-semibold px-6 py-4 rounded-xl">
                <Lock className="w-5 h-5" />
                {mobileLockStatus.reason || 'Locked'}
              </div>
            );
          }

          return (
            <button
              onClick={() => {
                // Use evidence gate for Step 3 navigation
                if (currentStep.step === 3) {
                  handleNavigateToStep3();
                } else {
                  navigate(currentStep.route);
                }
              }}
              className="w-full flex items-center justify-center gap-2 bg-slate-900 text-white font-semibold px-6 py-4 rounded-xl"
            >
              {currentStep.status === 'in_progress' ? 'Continue' : currentStep.status === 'completed' ? 'Review' : 'Start'} Step {currentStep.step}
              <ArrowRight className="w-5 h-5" />
            </button>
          );
        })()}
      </div>

      {/* Bottom spacer for mobile sticky CTA */}
      <div className="sm:hidden h-24" />

      {/* Appeal Outcome Form Modal */}
      {showOutcomeForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowOutcomeForm(false)}>
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <PartyPopper className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">Record Your Results</h2>
                    <p className="text-sm text-slate-500">How did your appeal go?</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowOutcomeForm(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>
            </div>

            {/* Form */}
            <div className="p-6 space-y-5">
              {/* Original Value */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  County's Original Assessed Value
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                  <input
                    type="number"
                    value={outcomeForm.originalValue}
                    onChange={(e) => setOutcomeForm(prev => ({ ...prev, originalValue: e.target.value }))}
                    placeholder="e.g., 350000"
                    className="w-full pl-8 pr-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">The value before your appeal</p>
              </div>

              {/* Final Value */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Final Assessed Value After Appeal
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                  <input
                    type="number"
                    value={outcomeForm.finalValue}
                    onChange={(e) => setOutcomeForm(prev => ({ ...prev, finalValue: e.target.value }))}
                    placeholder="e.g., 320000"
                    className="w-full pl-8 pr-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">The value after your meeting</p>
              </div>

              {/* Savings Preview */}
              {(() => {
                const savings = calculateDisplaySavings();
                if (savings && savings.savings > 0) {
                  return (
                    <div className="p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-200">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center flex-shrink-0">
                          <TrendingUp className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-emerald-800">Estimated Savings</p>
                          <p className="text-lg font-bold text-emerald-600">
                            ${savings.savings.toLocaleString()} assessment reduction
                          </p>
                          <p className="text-xs text-emerald-600/80">
                            ≈ ${Math.round(savings.taxSavings).toLocaleString()} annual tax savings
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Outcome */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Appeal Outcome
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 'won', label: 'Won', emoji: '🎉' },
                    { value: 'partial', label: 'Partial Win', emoji: '✓' },
                    { value: 'lost', label: 'No Change', emoji: '—' },
                    { value: 'pending', label: 'Still Pending', emoji: '⏳' },
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setOutcomeForm(prev => ({ ...prev, outcome: option.value }))}
                      className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                        outcomeForm.outcome === option.value
                          ? 'border-teal-500 bg-teal-50 text-teal-700'
                          : 'border-slate-200 hover:border-slate-300 text-slate-600'
                      }`}
                    >
                      <span className="mr-1">{option.emoji}</span> {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Notes (optional)
                </label>
                <textarea
                  value={outcomeForm.notes}
                  onChange={(e) => setOutcomeForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Any notes about your meeting experience..."
                  rows={3}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all resize-none"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
              <div className="flex gap-3">
                <button
                  onClick={() => setShowOutcomeForm(false)}
                  className="flex-1 px-4 py-3 border border-slate-200 rounded-lg text-slate-600 font-medium hover:bg-slate-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveOutcome}
                  disabled={savingOutcome || (!outcomeForm.originalValue && !outcomeForm.finalValue && !outcomeForm.outcome)}
                  className="flex-1 px-4 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingOutcome ? 'Saving...' : 'Save Results'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Evidence Packet Gate Dialog with Transition Video */}
      {property && (
        <EvidencePacketDialog
          isOpen={evidenceGate.isDialogOpen}
          propertyId={property.id}
          transitionVideoUrl={transitionVideoUrl}
          onClose={evidenceGate.hideDialog}
          onContinue={handleEvidenceDialogContinue}
          onSkip={handleEvidenceDialogSkip}
        />
      )}

      {/* Notice Received Dialog - for unlocking Step 2 */}
      {property && (
        <NoticeReceivedDialog
          isOpen={showNoticeDialog}
          propertyId={property.id}
          currentAssessedValue={property.assessedValue}
          onClose={() => setShowNoticeDialog(false)}
          onSuccess={handleNoticeReceived}
        />
      )}
    </div>
  );
};

export default StepsDashboardPage;
